import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import Input from '../shared/Input';
import Drawer from '../shared/Drawer';
import Autocomplete from '../shared/Autocomplete';
import Checkbox from '../shared/Checkbox';
import SelectBoxList from '../shared/select-box/SelectBoxList';
import SelectBox from '../shared/select-box/SelectBox';

import { userRoles } from '../../constants';
import countries from '../../constants/countries';

const countriesList = countries.map((c) => c.name);

const TeamModal = (props) => {
  const {
    mode = 'create',
    open,
    onClose,
    onSubmit,
    loading,
    usersList = [],
    proceduresList = [],
    initialValues,
  } = props;

  const [view, setView] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [countries, setCountries] = useState( []);
  const [users, setUsers] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [allProcedures, setAllProcedures] = useState([]);
  const [search, setSearch] = useState('');
  const [, setProceduresSearch] = useState('');

  useEffect(() => {
    if (open && mode === 'update') {
      setName(initialValues?.name || '');
      setDescription(initialValues?.description || '');
      setCountries(initialValues?.countries || []);
      setUsers(initialValues?.users?.map((uid) => usersList.find((u) => u.uid === uid)) || []);
      setProcedures(initialValues?.procedures?.map((id) => proceduresList.find((p) => p.id === id)) || []);
      setAllProcedures(initialValues?.allProcedures || false);
    }
  }, [open, mode, initialValues, usersList, proceduresList]);

  const resetValues = () => {
    setName('');
    setDescription('');
    setCountries([]);
    setUsers([]);
    setProcedures([]);
    setAllProcedures(false);
    setView(0);
  };

  const handleSubmit = async () => {
    const formObj = {
      name: name || '',
      description: description || '',
      countries: countries || [],
      users: users?.length ? users.map((u) => u.uid) : [],
      procedures: !allProcedures && procedures?.length ? procedures.map((p) => p.id) : [],
      allProcedures: !!allProcedures,
    };
    await onSubmit(formObj);
    resetValues();
  }

  const isDisabled = () => {
    return !name || !description;
  };

  const onUserSelect = (value) => {
    if (value && !users?.find((u) => u.uid === value?.uid)) {
      setUsers([...users, value]);
    }
  };

  const onUserRemove = (uid) => {
    setUsers([...users].filter((u) => u.uid !== uid));
    setSearch('');
  }

  const usersOptions = useMemo(() => {
    return usersList.filter((u) => u.role === userRoles.ENGINEER.name && !users?.find((user) => user?.uid === u?.uid));
  }, [usersList, users]);

  const onProcedureSelect = (value) => {
    if (value && !procedures?.find((p) => p.id === value?.id)) {
      setProcedures([...procedures, value]);
    }
  };

  const onProcedureRemove = (id) => {
    setProcedures([...procedures].filter((p) => p.id !== id));
    setProceduresSearch('');
  }

  const proceduresOptions = useMemo(() => {
    return proceduresList.filter((p) => !procedures?.find((procedure) => procedure?.id === p?.id));
  }, [proceduresList, procedures]);

  const renderView = () => {
    switch (view) {
      case 0:
        return (
          <Grid container spacing={1} sx={{ maxWidth: '560px' }}>
            <Grid size={{ xs: 12 }}>
              <Input
                id="name"
                type="text"
                placeholder=""
                value={name}
                onChange={(e) => setName(e.target.value)}
                label="Name"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Input
                id="description"
                type="text"
                placeholder=""
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                label="Description"
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                value={countries}
                onChange={setCountries}
                options={countriesList}
                label="Location"
                multiple
              />
            </Grid>

            <Divider sx={{ width: '100%', mt: 1, mb: 1 }} />

            <Grid size={{ xs: 12 }}>
              <Box>
                <Checkbox
                  checked={allProcedures}
                  onChange={(e) => setAllProcedures(e.target.checked)}
                  label="All Procedures"
                />
              </Box>
            </Grid>

            {!allProcedures && (
              <Grid size={{ xs: 12 }}>
                <SelectBox
                  label="Procedures"
                  selected={procedures?.map((p) => ({ key: p?.id, label: p?.name }))}
                  onClick={() => setView(1)}
                />
              </Grid>
            )}

            <Divider sx={{ width: '100%', mt: 2, mb: 1 }} />

            <Grid size={{ xs: 12 }}>
              <SelectBox
                label="Members"
                selected={users?.map((u) => ({ key: u?.uid, label: `${u.firstName} ${u.lastName}` }))}
                onClick={() => setView(2)}
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <SelectBoxList
            sx={{ width: '560px' }}
            title="Select Procedures"
            onBackClick={() => setView(0)}
            search={search}
            setSearch={setSearch}
            onChange={(selected) => {
              onProcedureSelect(selected);
              setSearch('');
            }}
            searchLabel="Add Procedure"
            options={proceduresOptions}
            selected={sortBy(procedures, 'name')}
            onRemoveItem={onProcedureRemove}
          />
        );
      case 2:
        return (
          <SelectBoxList
            sx={{ width: '560px' }}
            title="Select Members"
            onBackClick={() => setView(0)}
            search={search}
            setSearch={setSearch}
            onChange={(selected) => {
              onUserSelect(selected);
              setSearch('');
            }}
            getOptionLabel={(option) => `${option?.firstName} ${option?.lastName} (${option?.email})`}
            searchLabel="Add Engineer"
            options={usersOptions}
            selected={sortBy(users?.map((u) => ({ id: u.uid, ...u })), 'lastName')}
            onRemoveItem={onUserRemove}
            renderSelectedItem={(user) => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 14 }}>
                  {`${user.firstName} ${user.lastName}`}
                </Typography>
                <Typography sx={{ fontSize: 14, ml: 1, color: '#A4A7AE' }}>
                  {user.email}
                </Typography>
              </Box>
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        resetValues();
        onClose();
      }}
      title={mode === 'update' ? 'Edit Team' : 'Create Team'}
      subtitle="Enter team details"
      submitText={mode === 'update' ? 'Save Changes' : 'Create'}
      disabled={isDisabled()}
      loading={loading}
      onSubmit={handleSubmit}
    >
      <Box>
        {renderView()}
      </Box>
    </Drawer>
  );
};

TeamModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

export default TeamModal;
